import { useEffect, useRef, useState } from "react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Image as ImageIcon, Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Per-client logo upload. Uploads to /api/documents/leads/{lid}/logo and
 * previews from the same URL. Accepts PNG / JPG / SVG up to 5 MB.
 */
export default function LogoUpload({ leadId, hasLogo, onChange }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [bust, setBust] = useState(hasLogo ? Date.now() : 0);

  useEffect(() => { setBust(hasLogo ? Date.now() : 0); }, [hasLogo]);

  const url = `${process.env.REACT_APP_BACKEND_URL}/api/documents/leads/${leadId}/logo?v=${bust}`;

  async function pick(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/documents/leads/${leadId}/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Logo uploaded");
      onChange?.(true);
      setBust(Date.now());
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
    setBusy(false);
    if (inputRef.current) inputRef.current.value = "";
  }

  async function remove() {
    if (!window.confirm("Remove client logo?")) return;
    setBusy(true);
    try {
      await api.delete(`/documents/leads/${leadId}/logo`);
      onChange?.(false);
      toast.success("Logo removed");
    } catch { toast.error("Failed to remove"); }
    setBusy(false);
  }

  return (
    <div className="p-4 border border-slate-200 rounded-md" data-testid="client-logo-card">
      <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2 flex items-center gap-1.5">
        <ImageIcon className="w-3.5 h-3.5" />Client logo
      </p>
      <div className="flex items-center gap-4">
        <div className="w-24 h-24 border border-dashed border-slate-300 rounded flex items-center justify-center bg-slate-50 overflow-hidden">
          {hasLogo ? (
            <img src={url} alt="" className="max-w-full max-h-full object-contain" data-testid="client-logo-preview" />
          ) : (
            <ImageIcon className="w-8 h-8 text-slate-300" />
          )}
        </div>
        <div className="flex-1">
          <p className="text-xs text-slate-500 mb-2">
            PNG / JPG / SVG · Max 5 MB · Used across every generated document.
          </p>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={() => inputRef.current?.click()} disabled={busy} data-testid="upload-logo-btn">
              <Upload className="w-3.5 h-3.5 mr-1" />{hasLogo ? "Replace" : "Upload"}
            </Button>
            {hasLogo && (
              <Button size="sm" variant="outline" onClick={remove} disabled={busy} className="text-rose-600 hover:text-rose-700" data-testid="remove-logo-btn">
                <Trash2 className="w-3.5 h-3.5 mr-1" />Remove
              </Button>
            )}
          </div>
          <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/svg+xml" hidden onChange={pick} data-testid="logo-file-input" />
        </div>
      </div>
    </div>
  );
}
